import { useEffect, useRef, useState } from 'react';

import { Maybe } from 'types/graphql-api.generated';

import {
  HAS_DOLBY_UPSALE,
  TheaterReservationTracking,
  TrackingEventNames
} from 'common/constants/trackingEventsNames';
import eventEmitter from 'common/services/events/eventEmitter';
import request from 'common/tools/network/request';
import { path } from 'common/tools/network/routing';
import getShowtimesTechno from 'common/tools/showtime/getShowtimesTechno';
import getTicketingUrl from 'common/tools/showtime/getTicketingUrl';

import {
  PartialMovie,
  PartialProposal,
  PartialShowtime,
  PartialTheater,
  requestData
} from '../types';

interface Data {
  date?: Date;
  experiences: string[];
  loading: boolean;
  movie?: Maybe<PartialMovie>;
  showtime?: Maybe<PartialShowtime>;
  theater?: Maybe<PartialTheater>;
  ticketingUrl?: Maybe<string>;
}

export type Proposal = Maybe<
  PartialProposal & {
    date?: Date;
    experiences: string[];
    ticketingUrl?: Maybe<string>;
  }
>;

const useShowtimeData = (
  showtimeId: number,
  trackingContext: TheaterReservationTracking
) => {
  const [data, setData] = useState<Data>({
    experiences: [],
    loading: true
  });

  const [proposal, setProposal] = useState<Proposal>(null);

  const trackingData = useRef<TheaterReservationTracking>({
    ...trackingContext
  });

  useEffect(() => {
    request<requestData, { showtime: number }>(
      path('showtime_full_infos', { showtime: showtimeId })
    )
      .then(data => {
        let proposal: Maybe<PartialProposal> = null;
        if (data.showtime) {
          const { movie, theater, ...showtime } = data.showtime;
          setData({
            date: showtime.startsAt ? new Date(showtime.startsAt) : undefined,
            experiences: getShowtimesTechno(showtime),
            loading: false,
            movie,
            showtime,
            theater,
            ticketingUrl: getTicketingUrl(showtime.data?.ticketing)
          });
          trackingData.current.movie = {
            ...movie,
            ...trackingData.current.movie
          };
          trackingData.current.theater =
            theater ?? trackingData.current.theater;
          trackingData.current.showtime = {
            ...showtime,
            ...trackingData.current.showtime
          };
        }
        if (data.proposal && data.proposal.length > 0) {
          proposal = data.proposal[0];
          if (proposal) {
            setProposal({
              ...proposal,
              date: proposal.startsAt ? new Date(proposal.startsAt) : undefined,
              experiences: getShowtimesTechno(proposal),
              ticketingUrl: getTicketingUrl(proposal.data?.ticketing)
            });
          }
        }
        eventEmitter.emit(TrackingEventNames.OPEN_SHOWTIME_MODAL, {
          ...trackingData.current,
          ...(proposal
            ? {
                ga4dataOverwrite: {
                  has_dolby: HAS_DOLBY_UPSALE
                }
              }
            : {})
        });
      })
      .catch(() => setData(prev => ({ ...prev, loading: false })));
  }, [showtimeId]);

  return { data, proposal, trackingData };
};

export default useShowtimeData;
